<template>
  <CreateUpdateTemplate
    :routePreventDialog="routePreventDialog"
    :customClass="'customer-create create-form'"
    v-if="getPermission('lead:create')"
  >
    <template v-slot:header-title>
      <div class="d-flex align-items-center justify-content-between w-100">
        <h1 class="form-title">
          Create a new Lead
          <SystemGenerateId
            v-if="barcode && false"
            :system-id="barcode"
          ></SystemGenerateId>
        </h1>
        <v-chip
          label
          color="chip-custom-blue"
          outlined
          class="text-white p-3 mr-4"
          style="font-size: 21px !important; font-weight: 700"
        >
          {{ barcode }}
        </v-chip>
      </div>
    </template>

    <template v-slot:header-action>
      <v-btn
        v-on:click="goBack"
        :disabled="formLoading"
        outlined
        color="grey darken-2"
        class="mx-2 custom-grey-border custom-bold-button rounded-lg px-4 text-capitalize"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        Cancel
      </v-btn>

      <v-btn
        :disabled="!formValid || formLoading"
        :loading="formLoading"
        depressed
        color="cyan darken-1"
        class="mx-2 custom-bold-button white--text rounded-lg px-6 text-capitalize"
        v-on:click="onSubmit"
      >
        <v-icon left small>mdi-content-save-outline</v-icon>
        Save Lead
      </v-btn>
    </template>

    <template v-slot:body>
      <v-row dense>
        <!-- Lead Summary Sidebar -->
        <v-col cols="12" md="3" class="pr-md-2 pt-0 mb-4 mb-md-0">
          <v-card outlined class="rounded-lg pa-3 h-100 grey lighten-5">
            <perfect-scrollbar
              :options="{ suppressScrollX: true }"
              class="scroll pr-1"
              style="max-height: calc(100vh - 200px); position: relative"
            >
              <div class="d-flex align-center mb-3 px-1">
                <v-icon color="cyan darken-2" small class="mr-2"
                  >mdi-chart-timeline-variant</v-icon
                >
                <span
                  class="text-subtitle-1 font-weight-bold grey--text text--darken-4 text-uppercase"
                >
                  Lead Summary
                </span>
              </div>

              <!-- Lead Details Card -->
              <v-card
                outlined
                class="rounded-lg mb-3 white left-border-purple shadow-sm"
              >
                <div class="pa-3">
                  <div class="d-flex align-center mb-2 pb-1 border-bottom">
                    <v-icon small color="purple darken-1" class="mr-2"
                      >mdi-text-box-search-outline</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold purple--text text--darken-2"
                    >
                      Lead Details
                    </span>
                  </div>

                  <table width="100%" class="text-caption-1">
                    <tbody>
                      <tr>
                        <td
                          class="grey--text text--darken-1 font-size-18 font-weigt-500 py-1 font-weight-medium"
                        >
                          Name
                        </td>
                        <td
                          class="text-right font-weight-bold font-size-18 grey--text text--darken-3 py-1"
                        >
                          {{ basicInfo.lead_name || "—" }}
                        </td>
                      </tr>
                      <tr>
                        <td
                          class="grey--text text--darken-1 font-size-18 font-weigt-500 py-1 font-weight-medium"
                        >
                          Source
                        </td>
                        <td
                          class="text-right font-weight-bold font-size-18 grey--text text--darken-3 py-1"
                        >
                          {{ basicInfo.source || "—" }}
                        </td>
                      </tr>
                      <tr>
                        <td
                          class="grey--text text--darken-1 font-size-18 font-weigt-500 py-1 font-weight-medium"
                        >
                          Assign To
                        </td>
                        <td
                          class="text-right font-weight-bold font-size-18 grey--text text--darken-3 py-1"
                        >
                          {{ selectedUser ? selectedUser.display_name : "—" }}
                        </td>
                      </tr>
                      <tr>
                        <td
                          class="grey--text text--darken-1 font-size-18 font-weigt-500 py-1 font-weight-medium"
                        >
                          Lead Date
                        </td>
                        <td
                          class="text-right font-weight-bold font-size-18 grey--text text--darken-3 py-1"
                        >
                          {{ formatDate(basicInfo.lead_date) || "—" }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card>

              <!-- Customer Details Card -->
              <v-card
                outlined
                class="rounded-lg mb-3 white left-border-blue shadow-sm"
              >
                <div class="pa-3">
                  <div class="d-flex align-center mb-2 pb-1 border-bottom">
                    <v-icon small color="blue darken-1" class="mr-2"
                      >mdi-account-tie-outline</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold blue--text text--darken-2"
                    >
                      Customer Details
                    </span>
                  </div>

                  <div class="text-caption-1 grey--text text--darken-1 mb-1">
                    Type:
                    <span class="font-weight-bold black--text text-capitalize">
                      <v-chip
                        v-if="basicInfo.company_type === 'company'"
                        color="blue"
                        class="text-white p-3 mr-5 cursor-pointer"
                        small
                        >Company</v-chip
                      >
                      <v-chip
                        v-else
                        color="red"
                        class="text-white p-3 cursor-pointer"
                        small
                        >Individual</v-chip
                      >
                    </span>
                  </div>

                  <div class="grey--text text--darken-2 text-caption-1">
                    <div
                      class="d-flex align-center mb-1 hover-effect"
                      v-if="basicInfo.email"
                    >
                      <v-icon x-small color="blue darken-1" class="mr-2"
                        >mdi-email-outline</v-icon
                      >
                      <span class="text-truncate">{{ basicInfo.email }}</span>
                    </div>
                    <div
                      class="d-flex align-center mb-1 hover-effect"
                      v-if="basicInfo.phone_number"
                    >
                      <v-icon x-small color="blue darken-1" class="mr-2"
                        >mdi-phone-outline</v-icon
                      >
                      <span>{{ basicInfo.phone_number }}</span>
                    </div>
                    <div
                      class="d-flex align-center hover-effect"
                      v-if="basicInfo.website"
                    >
                      <v-icon x-small color="blue darken-1" class="mr-2"
                        >mdi-web</v-icon
                      >
                      <span class="text-truncate">{{ basicInfo.website }}</span>
                    </div>
                  </div>
                </div>
              </v-card>

              <!-- Address Information Card -->
              <v-card
                outlined
                class="rounded-lg mb-3 white left-border-orange shadow-sm"
                v-if="basicInfo.address_line_1 || basicInfo.postal_code"
              >
                <div class="pa-3">
                  <div class="d-flex align-center mb-2 pb-1 border-bottom">
                    <v-icon small color="orange darken-2" class="mr-2"
                      >mdi-map-marker-outline</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold orange--text text--darken-3"
                    >
                      Address Information
                    </span>
                  </div>

                  <div class="text-caption-1 grey--text text--darken-3">
                    <div
                      v-if="basicInfo.address_line_1"
                      class="font-weight-medium"
                    >
                      {{ basicInfo.address_line_1 }}
                    </div>
                    <div v-if="basicInfo.address_line_2">
                      {{ basicInfo.address_line_2 }}
                    </div>
                    <div
                      v-if="basicInfo.unit_no"
                      class="grey--text text--darken-1"
                    >
                      Unit: {{ basicInfo.unit_no }}
                    </div>
                    <div
                      v-if="basicInfo.postal_code"
                      class="grey--text text--darken-1"
                    >
                      Postal Code: {{ basicInfo.postal_code }}
                    </div>
                    <div
                      v-if="basicInfo.country"
                      class="text-capitalize font-weight-bold mt-1"
                    >
                      {{ basicInfo.country }}
                    </div>
                  </div>
                </div>
              </v-card>

              <!-- Description Card -->
              <v-card
                outlined
                class="rounded-lg mb-3 white left-border-green shadow-sm"
                v-if="basicInfo.internal_remark"
              >
                <div class="pa-3">
                  <div class="d-flex align-center mb-2 pb-1 border-bottom">
                    <v-icon small color="green darken-1" class="mr-2"
                      >mdi-text-subject</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold green--text text--darken-2"
                    >
                      Description
                    </span>
                  </div>

                  <div class="text-caption-1 grey--text text--darken-3">
                    {{ basicInfo.internal_remark }}
                  </div>
                </div>
              </v-card>

              <!-- Contact Persons Summary Card -->
              <v-card
                outlined
                class="rounded-lg mb-3 white left-border-indigo shadow-sm"
                v-if="contactPersons.length > 0"
              >
                <div class="pa-3">
                  <div
                    class="d-flex align-center mb-2 pb-1 border-bottom justify-space-between"
                  >
                    <div class="d-flex align-center">
                      <v-icon small color="indigo darken-1" class="mr-2"
                        >mdi-account-multiple-outline</v-icon
                      >
                      <span
                        class="text-subtitle-1 font-weight-bold indigo--text text--darken-2"
                      >
                        Contact Persons
                      </span>
                    </div>
                    <v-chip
                      x-small
                      color="indigo lighten-4"
                      text-color="indigo darken-3"
                      class="font-weight-bold"
                    >
                      {{ contactPersons.length }}
                    </v-chip>
                  </div>

                  <div
                    v-for="(person, index) in contactPersons"
                    :key="index"
                    class="mb-2 last-mb-0"
                  >
                    <v-card flat class="pa-2 grey lighten-5 rounded-lg border">
                      <div class="font-weight-bold text-caption-1 black--text">
                        {{ person.name || person.display_name }}
                      </div>
                      <div
                        class="text-caption-1 grey--text text--darken-2"
                        v-if="person.email"
                      >
                        <v-icon x-small color="indigo darken-1" class="mr-1"
                          >mdi-email-outline</v-icon
                        >
                        {{ person.email }}
                      </div>
                      <div
                        class="text-caption-1 grey--text text--darken-2"
                        v-if="person.phone"
                      >
                        <v-icon x-small color="indigo darken-1" class="mr-1"
                          >mdi-phone-outline</v-icon
                        >
                        {{ person.phone }}
                      </div>
                    </v-card>
                  </div>
                </div>
              </v-card>
            </perfect-scrollbar>
          </v-card>
        </v-col>

        <!-- Main Content Column -->
        <v-col cols="12" md="9" class="pl-md-2 pt-0">
          <v-form
            ref="leadForm"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="onSubmit"
          >
            <!-- Tab Navigation with Arrow Design -->
            <v-card class="pa-3 rounded-lg mb-3 lead-tab-navigation">
              <v-btn-toggle
                v-model="leadTab"
                mandatory
                borderless
                class="my-0 w-100 lead-main-tabs"
                min-height="38"
              >
                <v-btn
                  value="details"
                  class="flex-grow-1 font-weight-bold text-capitalize"
                  depressed
                  dark
                  :class="{ active: leadTab === 'details' }"
                  min-height="38"
                >
                  <v-icon left small>mdi-information-outline</v-icon>
                  Lead Information
                </v-btn>

                <v-btn
                  value="contact-person"
                  class="flex-grow-1 font-weight-bold text-capitalize"
                  depressed
                  dark
                  :class="{ active: leadTab === 'contact-person' }"
                  min-height="38"
                >
                  <v-icon left small>mdi-account-multiple-outline</v-icon>
                  Contact Persons
                </v-btn>
              </v-btn-toggle>
            </v-card>

            <!-- Tab Content -->
            <div v-show="leadTab === 'details'">
              <BasicDetails
                ref="basicDetails"
                :detail="basicInfo"
                :options="options"
                @update:detail="(val) => Object.assign(basicInfo, val)"
              ></BasicDetails>
            </div>

            <div v-show="leadTab === 'contact-person'">
              <ContactPersons
                v-on:saveContactPerson="updateContactPerson"
              ></ContactPersons>
            </div>
          </v-form>
        </v-col>
      </v-row>
    </template>
  </CreateUpdateTemplate>
</template>

<script>
import { mapGetters } from "vuex";
import CommonMixin from "@/core/plugins/common-mixin";
import CreateUpdateTemplate from "@/view/pages/partials/Create-Update-Template.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  POST,
  CLEAR_ERROR,
  GET,
  QUERY,
} from "@/core/services/store/request.module";
import SystemGenerateId from "@/view/pages/partials/SystemGenerateId.vue";
import BasicDetails from "@/view/pages/lead-new/create-or-update/Basic-Details.vue";
import ContactPersons from "@/view/pages/lead-new/create-or-update/Contact-Persons.vue";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
// import { PreventDialogEventBus } from "@/core/lib/prevent.reload.lib";

export default {
  mixins: [CommonMixin],
  name: "lead-create",
  title: "Create Lead",
  data() {
    return {
      leadId: 0,
      lead: {},
      propertyAddress: {},
      barcode: null,
      basicDetail: {},
      contactDetail: {},
      contactPersons: [],
      notification: {},
      userList: [],
      basicInfo: {
        id: null,
        lead_type: "new",
        status: null,
        source: null,
        opportunity: null,
        product_type: null,
        assign_to: null,
        amount: null,
        lead_name: null,
        company_name: null,
        company_type: "company",
        lead_date: null,
        position: null,
        postal_code: null,
        email: null,
        address_line_1: null,
        address_line_2: null,
        unit_no: null,
        zip: null,
        country: null,
        phone_number: null,
        website: null,
        internal_remark: null,
        email_reply: null,
        is_public: 0,
        contacted_today: 1,
        isLeadCreated: false,
        attachments: [],
      },
      leadTab: "details",
      options: {},
    };
  },
  components: {
    BasicDetails,
    ContactPersons,
    CreateUpdateTemplate,
    SystemGenerateId,
  },
  watch: {
    leadTab() {
      const _this = this;
      setTimeout(function () {
        _this.$refs.leadForm.validate();
      }, 200);
    },
    formValid(param) {
      if (param) {
        this.$store.dispatch(CLEAR_ERROR, {});
      }
    },
  },
  computed: {
    ...mapGetters(["gCreateFormFields"]),
    selectedUser() {
      if (this.userList && this.basicInfo.assign_to) {
        return this.userList.find(
          (user) => user.id === this.basicInfo.assign_to
        );
      }
      return null;
    },
  },
  methods: {
    formatAmount(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },

    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },

    updateContactPerson(param) {
      this.contactPersons = param;
    },

    getOptions() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "customer/options" })
        .then(({ data }) => {
          _this.options = data.options;
          _this.barcode = data.barcode;
          _this.options.allow_zones = data.allow_zones;
          _this.options.active_zones = data.active_zones;
          _this.options.all_tags = data.all_tags;
          _this.options.all_cms_categories = data.all_cms_categories;
        })
        .catch((error) => {
          _this.logError(error);
        });

      // Fetch companies for dropdown
      _this.$store
        .dispatch(QUERY, {
          url: "customer",
          data: {
            entity: 1,
            status: "active",
            all: true,
            filter_type: "company",
          },
        })
        .then(({ data }) => {
          _this.$set(_this.options, "companies", data.rows || data || []);
          console.log("Companies fetched:", _this.options.companies);
        })
        .catch((error) => {
          _this.logError(error);
        });
    },

    async getUserList() {
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: "lead/options",
        });
        if (data && data.users) {
          this.userList = data.users;
        }
      } catch (error) {
        this.logError(error);
      }
    },

    async onSubmit() {
      const _this = this;

      try {
        const formErrors = _this.validateForm(_this.$refs.leadForm);
        if (formErrors && formErrors.length) {
          for (let i = 0; i < formErrors.length; i++) {
            ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
          }
          return false;
        }

        if (!_this.$refs.leadForm.validate()) {
          return false;
        }

        _this.$store.dispatch(CLEAR_ERROR, {});

        const formData = {
          id: _this.basicInfo?.id || null,
          lead_type: _this.basicInfo?.lead_type || "new",
          status: _this.basicInfo?.status || null,
          source: _this.basicInfo?.source || null,
          company_type: _this.basicInfo?.company_type || "company",
          opportunity: _this.basicInfo?.opportunity || null,
          product_type: _this.basicInfo?.product_type || null,
          assign_to: _this.basicInfo?.assign_to || null,
          amount: _this.basicInfo?.amount || null,
          lead_name: _this.basicInfo?.lead_name || null,
          company_name: _this.basicInfo?.company_name || null,
          lead_date: _this.basicInfo?.lead_date || null,
          position: _this.basicInfo?.position || null,
          postal_code: _this.basicInfo?.postal_code || null,
          email: _this.basicInfo?.email || null,
          address_line_1: _this.basicInfo?.address_line_1 || null,
          address_line_2: _this.basicInfo?.address_line_2 || null,
          unit_no: _this.basicInfo?.unit_no || null,
          country: _this.basicInfo?.country || null,
          phone_number: _this.basicInfo?.phone_number || null,
          website: _this.basicInfo?.website || null,
          internal_remark: _this.basicInfo?.internal_remark || null,
          email_reply: _this.basicInfo?.email_reply || null,
          is_public: _this.basicInfo?.is_public || 0,
          contacted_today: _this.basicInfo?.contacted_today || 0,
          persons: _this.contactPersons || [],
          attachments: _this.basicInfo?.attachments || [],
        };

        _this.formLoading = true;
        try {
          if (!_this.isLeadCreated) {
            let leadObject = await _this.$store.dispatch(POST, {
              url: "lead",
              data: formData,
            });
            _this.leadId = leadObject.data.id;
            _this.isLeadCreated = true;
          }
        } catch (error) {
          _this.formLoading = false;
          ErrorEventBus.$emit(
            "update:error",
            "Lead is not created. Please try again."
          );
          return false;
        }

        if (_this.isLeadCreated) {
          _this.$router.push(
            _this.getDefaultRoute("lead.detail", {
              params: { id: _this.leadId },
            })
          );
        }
      } catch (error) {
        ErrorEventBus.$emit("update:error", InitializeError(error));
        _this.formLoading = false;
        return false;
      }
    },

    getLead() {
      const _this = this;
      _this.$store
        .dispatch(GET, {
          url: "leads/" + _this.leadId,
        })
        .then(({ data }) => {
          _this.lead = data;
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
  },
  mounted() {
    this.getOptions();
    this.getUserList();

    this.$refs.leadForm.validate();

    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Lead", route: "lead" },
      { title: "Create" },
    ]);

    this.leadId = this.lodash.toSafeInteger(this.$route.query.lead);
    if (this.leadId > 0) {
      this.getLead();
    }
  },
  // beforeRouteLeave(to, from, next) {
  //   const _this = this;
  //   if (_this.isLeadCreated) {
  //     next();
  //   } else {
  //     _this.routePreventDialog = true;
  //     PreventDialogEventBus.$on("stay-on-page", function () {
  //       _this.routePreventDialog = false;
  //       next(false);
  //     });
  //     PreventDialogEventBus.$on("leave-page", function () {
  //       _this.routePreventDialog = false;
  //       next();
  //     });
  //   }
  // },
};
</script>

<style scoped>
/* Summary sidebar styling */
.left-border-blue {
  border-left: 4px solid #1e88e5 !important;
}

.left-border-green {
  border-left: 4px solid #43a047 !important;
}

.left-border-orange {
  border-left: 4px solid #fb8c00 !important;
}

.left-border-purple {
  border-left: 4px solid #8e24aa !important;
}

.left-border-indigo {
  border-left: 4px solid #3f51b5 !important;
}

.hover-effect {
  cursor: pointer;
}

.hover-effect:hover {
  color: #00bcd4 !important;
}

.lead-tab-navigation {
  position: relative;
  z-index: 2;
  background-color: #fff !important;
}

/* Main Tab Navigation Styling - Arrow Design */
.lead-main-tabs .v-btn {
  background: #e0f7fa !important;
  color: #00838f !important;
  border-radius: 0 !important;
  margin: 0 !important;
  font-size: 14px;
  font-weight: 600 !important;
  position: relative;
  transition: all 0.3s ease;
}

.lead-main-tabs .v-btn::after {
  content: "";
  position: absolute;
  right: -20px;
  top: 0;
  width: 0;
  height: 0;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
  border-left: 20px solid #e0f7fa;
  z-index: 1;
  transition: all 0.3s ease;
}

.lead-main-tabs .v-btn.active {
  background: #00bcd4 !important;
  color: #fff !important;
}

.lead-main-tabs .v-btn.active::after {
  border-left-color: #00bcd4;
}

.lead-main-tabs .v-btn:first-child {
  border-radius: 8px 0 0 8px !important;
}

.lead-main-tabs .v-btn:last-child {
  border-radius: 0 8px 8px 0 !important;
}

.lead-main-tabs .v-btn:last-child::after {
  display: none;
}
</style>
